import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { ConnectedRouter } from 'connected-react-router';
import { ToastContainer } from 'react-toastify';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { AssessmentProvider } from '@learnosityassessment';
import { QueryClientProvider } from 'react-query';
import * as Sentry from '@sentry/react';
import ThemeProvider from '@Theme';
import { datadogRum } from '@datadog/browser-rum';
import ReactQueryClientHelper, { queryClient } from '@utils/queryClientHelper';
import { buildRumProxy } from '@utils/rumProxy';
import { LocalizationProvider } from '@mui/x-date-pickers';
// If you are using date-fns v3.x or v4.x, please import `AdapterDateFns`
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { configTokenInterceptor } from '@apis/config';
import { InteractionContextProvider } from '@components/ElementAnalytics/InteractionContext';
import history from './utils/history';
import Routes from './routes';
import configureStore from './store/configureStore';
import { MathJaxProvider } from './components/MathJaxContainer';
import { globalVars } from './globals';
import SentryChalkTalk from './utils/SentryChalkTalk';
import '../public/assets/scss/main.scss';
import '../public/assets/scss/fonts-site.scss';
import ChalkTalkErrorBoundary from './components/ChalkTalkErrorBoundary/ChalkTalkErrorBoundary';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';

export const { store, persistor } = configureStore({}, history);

globalVars.store = store;
globalVars.persistor = persistor;

if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging') {
  SentryChalkTalk.init();
  datadogRum.init({
    applicationId: 'c189adb9-46ac-42cb-bf08-65e5dacdfa31',
    clientToken: 'pub2e60c0a4094e9bc4793e459f15e852fa',
    site: 'us5.datadoghq.com',
    service: process.env.NODE_ENV === 'production' ? 'chalktalk-prod' : 'chalktalk-staging',
    env: process.env.NODE_ENV === 'production' ? 'prod' : 'staging',
    version: process.env.BUILD_VERSION?.toLowerCase() ?? 'dev-local',
    sessionSampleRate: 100,
    premiumSampleRate: 50,
    tracesSampleRate: 20,
    sessionReplaySampleRate: 50,
    trackUserInteractions: true,
    trackFrustrations: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    trackSessionAcrossSubdomains: true,
    // Some school device filters block Datadog's domain; send through our own host instead
    proxy: buildRumProxy(process.env.DD_RUM_PROXY_HOST),
    allowFallbackToLocalStorage: true,
  });
}

navigator.serviceWorker?.getRegistrations().then((registrations) => {
  console.log(' unregister start');
  for (const registration of registrations) {
    console.log('registration: ', registration);
    registration.unregister();
  }
});

const App = () => (
  <ThemeProvider>
    <InteractionContextProvider interactionArea="ct">
      <ChalkTalkErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <ConnectedRouter history={history}>
                <DndProvider backend={HTML5Backend}>
                  <div
                    id="toast-live-polite"
                    aria-live="polite"
                    aria-atomic="true"
                    style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}
                  />
                  <div
                    id="toast-live-assertive"
                    aria-live="assertive"
                    aria-atomic="true"
                    style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}
                  />
                  <aside aria-label="Notifications" aria-live="off">
                    <ToastContainer
                      style={{ width: '440px', padding: '0px', top: '4em' }}
                      autoClose={5000}
                      closeButton={false}
                    />
                  </aside>
                  <MathJaxProvider>
                    <AssessmentProvider
                      serviceURL={{
                        normal: process.env.ASSESSMENT_SERVICE_URL,
                        CDN: process.env.ASSESSMENT_CDN_SERVICE_URL,
                      }}
                      serviceEnvironment={process.env.ASSESSMENT_SERVICE_ENVIRONMENT}
                    >
                      <ReactQueryClientHelper />
                      <ScrollToTop />
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Routes history={history} />
                      </LocalizationProvider>
                    </AssessmentProvider>
                  </MathJaxProvider>
                </DndProvider>
              </ConnectedRouter>
            </PersistGate>
          </Provider>
        </QueryClientProvider>
      </ChalkTalkErrorBoundary>
    </InteractionContextProvider>
  </ThemeProvider>
);
const AppWithSentry = Sentry.withProfiler(App);
const root = createRoot(document.getElementById('app'));
root.render(<AppWithSentry />);
configTokenInterceptor(store);
